import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserService,
  CheckDomainParams,
  UserLoginParams,
  UserAuthResult,
  SystemService
} from '@/api';
import { useTabAuth } from '@/hooks/useTabAuth';
import { UserInfo } from '@/api/types/user';
import { logPrint } from '@/utils/my-utils';

/**
 * 认证相关的hook
 * 处理组织代码验证、用户登录等认证逻辑
 */
export function useAuth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  const tabAuth = useTabAuth();

  /**
   * 处理租户权限信息获取和用户状态更新
   * @param userInfo 用户基本信息
   * @param accessToken 访问令牌
   * @param phone 手机号
   */
  const handleTenantPermissions = async (
    userInfo: UserInfo,
    _accessToken: string,
    _phone: string
  ): Promise<void> => {
    try {
      // 获取租户权限信息
      const tenantPermissions = await UserService.getUserTenantPermissions();

      // 保存租户ID
      if (tenantPermissions.tenant_id) {
        tabAuth.setTenantId(tenantPermissions.tenant_id);
      }

      userInfo.role = tenantPermissions.role;
      userInfo.permissions = tenantPermissions.permissions;
      // 保存完整用户信息
      tabAuth.setUserInfo(userInfo);

      logPrint('用户认证成功');
    } catch (tenantError) {
      console.error('获取租户权限失败:', tenantError);

      // 降级处理：使用基本用户信息
      tabAuth.setUserInfo(userInfo);

      logPrint('用户认证成功（降级处理）');
    }
  };

  /**
   * 验证组织代码
   * @param domain 组织域名/代码
   * @returns Promise<boolean> 是否有效
   */
  const validateOrganizationCode = async (domain: string): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    try {
      SystemService.healthCheck();

      const params: CheckDomainParams = { domain };
      const result = await UserService.checkDomain(params);

      // 如果返回了tenant_id，说明域名存在（组织代码有效）
      if (result.tenant_id) {
        // 组织代码有效，保存到标签页认证数据中
        tabAuth.setOrganizationCode(domain);
        // 同时保存租户ID
        tabAuth.setTenantId(result.tenant_id);
        return true;
      } else {
        setError('组织代码不存在，请检查后重试');
        return false;
      }
    } catch (err: any) {
      console.error('验证组织代码失败:', err);
      if (err?.code === 800004) {
        setError('组织代码不存在，请检查后重试');
        return false;
      } else if (err?.err_msg) {
        setError(err.err_msg);
      } else {
        setError('验证失败，请检查网络连接后重试');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 用户登录
   * @param phone 手机号
   * @param password 密码
   * @param countryCode 国家代码（可选，默认为86）
   * @returns Promise<boolean> 是否登录成功
   */
  const userLogin = async (
    phone: string,
    password: string,
    countryCode: string = '1'
  ): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    try {
      const loginParams: UserLoginParams = {
        country_code: countryCode,
        phone,
        password
      };

      const result: UserAuthResult = await UserService.login(loginParams);

      // 解析登录响应数据
      const { token: accessToken, user: userInfo, encryption_key: encryptionKey } = result;

      // 保存基本认证信息
      tabAuth.setLoginData({
        accessToken,
        userId: userInfo.id,
        encryptionKey
      });

      // 尝试获取租户权限信息
      await handleTenantPermissions(userInfo, accessToken, phone);
      return true;
    } catch (err: any) {
      console.error('登录失败:', err);

      // 根据错误码提供更友好的错误信息
      if (err?.code === 100002) {
        setError('用户不存在');
      } else if (err?.code === 800001 || err?.code === 403) {
        setError('密码错误');
      } else if (err?.err_msg) {
        setError(err.err_msg);
      } else {
        setError('登录失败，请检查手机号和密码');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 跳转到登录页面
   * @param organizationCode 组织代码
   */
  const navigateToLogin = (organizationCode: string) => {
    navigate(`/auth/login?org=${encodeURIComponent(organizationCode)}`);
  };

  /**
   * 跳转到主页面
   */
  const navigateToMain = () => {
    navigate('/main');
  };

  /**
   * 用户登出
   */
  const userLogout = async (): Promise<void> => {
    try {
      // 调用登出API
      await UserService.logout();
    } catch (err) {
      console.error('登出API调用失败:', err);
      // 即使API失败，也要清除本地认证信息
    } finally {
      // 清除标签页独立的认证信息
      tabAuth.clearAuth();
    }
  };

  /**
   * 获取用户信息
   * @param q 搜索查询（用户ID、handle或昵称，可选）
   * @returns Promise<UserInfo | null> 用户信息
   */
  const getUserInfo = async (q: string): Promise<UserInfo | null> => {
    setIsLoading(true);
    setError('');

    try {
      const userInfo = await UserService.getUserInfo(q);
      logPrint('用户信息获取成功:', userInfo);
      return userInfo;
    } catch (err: any) {
      console.error('获取用户信息失败:', err);

      if (err?.code === 100002) {
        setError('用户不存在');
      } else if (err?.err_msg) {
        setError(err.err_msg);
      } else {
        setError('获取用户信息失败，请检查网络连接');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 刷新当前用户信息
   * @returns Promise<boolean> 是否刷新成功
   */
  const refreshUserInfo = async (): Promise<boolean> => {
    let localUserInfo = tabAuth.getUserInfo();
    const loginUserId = localUserInfo?.id || '';
    const userInfo = await getUserInfo(loginUserId.toString());
    if (userInfo) {
      localUserInfo = {
        ...localUserInfo,
        ...userInfo
      };
      // 更新标签页认证数据中的用户信息
      tabAuth.setUserInfo(localUserInfo);
    }

    return userInfo !== null;
  };

  /**
   * 清除错误信息
   */
  const clearError = () => {
    setError('');
  };

  return {
    // 状态
    isLoading,
    error,

    // 方法
    validateOrganizationCode,
    userLogin,
    userLogout,
    getUserInfo,
    refreshUserInfo,
    navigateToLogin,
    navigateToMain,
    clearError
  };
}
